import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSubjectList } from '../../../hooks/useSubjectList';
import { MasteryRecord } from '../../../hooks/useQuizMasteryDB';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
}

export type MasteryFilter =
  | 'all'
  | 'unlearned'
  | 'learning'
  | 'mastered'
  | 'incorrect'
  | 'bookmarked';

interface QuizStartConfigProps {
  totalCount: number;
  questions: QuizQuestion[];
  onStart: (
    subject: string,
    subcategory: string,
    count: number,
    masteryFilter: MasteryFilter
  ) => void;
  onClose: () => void;
  masteryRecord: MasteryRecord;
  initialMasteryFilter?: MasteryFilter;
}

/**
 * QuizStartConfig component – a modal that lets the user pick
 * subject / sub‑category, mastery filter and number of questions
 * before starting a quiz.
 *
 * The component is deliberately defensive:
 *  - All state updates are guarded against invalid values.
 *  - `handleStart` validates that there is at least one question
 *    matching the current filters before invoking `onStart`.
 *  - Any unexpected errors while rendering are caught by React
 *    error boundaries (outside of this component).
 */
export default function QuizStartConfig({
  totalCount,
  questions,
  onStart,
  onClose,
  masteryRecord,
  initialMasteryFilter,
}: QuizStartConfigProps) {
  const { subjects: allSubjectNames } = useSubjectList();
  const [subject, setSubject] = useState<string>('');
  const [subcategory, setSubcategory] = useState<string>('');
  const [count, setCount] = useState<number>(10);
  const [masteryFilter, setMasteryFilter] = useState<MasteryFilter>(
    initialMasteryFilter ?? 'all'
  );

  // サブカテゴリをクイズの実データから動的に取得
  const currentSubcategories = useMemo(() => {
    if (!subject) return [];
    const subs = new Set<string>();
    questions.forEach((q) => {
      if (q.subject === subject && q.subcategory) {
        subs.add(q.subcategory);
      }
    });
    return Array.from(subs).sort().map((name) => ({ name }));
  }, [subject, questions]);

  const showSubcategory = currentSubcategories.length > 0;

  // Number of questions that satisfy the current filter set.
  const filteredCount = useMemo(() => {
    let pool = questions;

    if (subject) pool = pool.filter((q) => q.subject === subject);
    if (subcategory) pool = pool.filter((q) => q.subcategory === subcategory);

    switch (masteryFilter) {
      case 'unlearned':
        pool = pool.filter(
          (q) => !(q.id in masteryRecord) || masteryRecord[q.id]?.correct_count === 0
        );
        break;
      case 'learning':
        pool = pool.filter((q) => (masteryRecord[q.id]?.correct_count ?? 0) === 1);
        break;
      case 'mastered':
        pool = pool.filter((q) => (masteryRecord[q.id]?.correct_count ?? 0) >= 2);
        break;
      case 'incorrect':
        pool = pool.filter((q) => (masteryRecord[q.id]?.incorrect_count ?? 0) > 0);
        break;
      case 'bookmarked':
        pool = pool.filter((q) => masteryRecord[q.id]?.is_bookmarked === true);
        break;
      // 'all' falls through unchanged
    }

    return pool.length;
  }, [questions, subject, subcategory, masteryFilter, masteryRecord]);

  // Count of questions per mastery category – used for UI badges.
  const masteryCounts = useMemo(() => {
    let pool = questions;
    if (subject) pool = pool.filter((q) => q.subject === subject);
    if (subcategory) pool = pool.filter((q) => q.subcategory === subcategory);

    const unlearned = pool.filter(
      (q) => !(q.id in masteryRecord) || masteryRecord[q.id]?.correct_count === 0
    ).length;
    const incorrect = pool.filter(
      (q) => (masteryRecord[q.id]?.incorrect_count ?? 0) > 0
    ).length;
    const learning = pool.filter(
      (q) => (masteryRecord[q.id]?.correct_count ?? 0) === 1
    ).length;
    const mastered = pool.filter(
      (q) => (masteryRecord[q.id]?.correct_count ?? 0) >= 2
    ).length;
    const bookmarked = pool.filter(
      (q) => masteryRecord[q.id]?.is_bookmarked === true
    ).length;

    return {
      all: pool.length,
      unlearned,
      learning,
      mastered,
      incorrect,
      bookmarked,
    };
  }, [questions, subject, subcategory, masteryRecord]);

  const handleSelectSubject = (name: string) => {
    if (subject === name) {
      setSubject('');
      setSubcategory('');
    } else {
      setSubject(name);
      setSubcategory('');
    }
  };

  const handleStart = () => {
    if (filteredCount === 0) return;
    const safeCount = count === 9999 ? filteredCount : Math.min(count, filteredCount);
    onStart(subject, subcategory, safeCount, masteryFilter);
  };

  const filterLabel = subcategory
    ? `${subject} > ${subcategory}`
    : subject
    ? subject
    : '全科目';

  const masteryFilterLabels: {
    key: MasteryFilter;
    label: string;
    icon: string;
    color: string;
  }[] = [
    { key: 'all', label: 'すべて', icon: 'ri-apps-line', color: 'teal' },
    {
      key: 'bookmarked',
      label: '苦手な問題',
      icon: 'ri-bookmark-fill',
      color: 'amber',
    },
    {
      key: 'incorrect',
      label: '間違えた問題',
      icon: 'ri-close-circle-line',
      color: 'red',
    },
    { key: 'unlearned', label: '未学習', icon: 'ri-question-line', color: 'gray' },
    {
      key: 'learning',
      label: '学習中',
      icon: 'ri-loader-4-line',
      color: 'gray',
    },
    {
      key: 'mastered',
      label: '習熟済',
      icon: 'ri-checkbox-circle-line',
      color: 'teal',
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 999999 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        style={{ zIndex: 1000000 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-xl">
              <i className="ri-gamepad-line text-lg text-teal-600"></i>
            </div>
            <h3 className="text-base font-bold text-gray-800">クイズに挑戦</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Subject selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-folder-line mr-1.5 text-teal-600 text-sm"></i>
              科目を選択
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setSubject('');
                  setSubcategory('');
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  subject === ''
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                すべて
              </button>
              {allSubjectNames.map((name) => (
                <button
                  key={name}
                  onClick={() => handleSelectSubject(name)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1 ${
                    subject === name
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  {name}
                  {questions.some((q) => q.subject === name && q.subcategory) && (
                    <i
                      className={`ri-arrow-down-s-line text-xs transition-transform ${
                        subject === name ? 'rotate-180' : ''
                      }`}
                    ></i>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Subcategory selection */}
          {showSubcategory && currentSubcategories.length > 0 && (
            <div className="pl-3 border-l-2 border-teal-400">
              <label className="block text-xs font-medium text-gray-500 mb-2">
                <i className="ri-price-tag-3-line mr-1 text-teal-500"></i>
                {subject}のカテゴリ
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                <button
                  onClick={() => setSubcategory('')}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    subcategory === ''
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  すべて
                </button>
                {currentSubcategories.map((sub) => (
                  <button
                    key={sub.name}
                    onClick={() =>
                      setSubcategory(sub.name === subcategory ? '' : sub.name)
                    }
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      subcategory === sub.name
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-200 border border-gray-200'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mastery filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-bar-chart-box-line mr-1.5 text-teal-600 text-sm"></i>
              習熟度で絞り込み
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {masteryFilterLabels.map((f) => {
                const c = masteryCounts[f.key];
                const isActive = masteryFilter === f.key;
                const isIncorrect = f.key === 'incorrect';
                const isBookmarked = f.key === 'bookmarked';
                return (
                  <button
                    key={f.key}
                    onClick={() => setMasteryFilter(f.key)}
                    className={`flex flex-col items-center justify-center gap-1 px-1.5 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap border h-20 ${
                      isActive
                        ? isBookmarked
                          ? 'bg-amber-50 border-amber-300 text-amber-700 ring-1 ring-amber-200'
                          : isIncorrect
                          ? 'bg-red-50 border-red-300 text-red-700 ring-1 ring-red-200'
                          : 'bg-teal-50 border-teal-300 text-teal-700 ring-1 ring-teal-200'
                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    <i
                      className={`${f.icon} text-base ${
                        isActive
                          ? isBookmarked
                            ? 'text-amber-500'
                            : isIncorrect
                            ? 'text-red-500'
                            : 'text-teal-600'
                          : 'text-gray-400'
                      }`}
                    ></i>
                    <span className="text-[10px] sm:text-xs leading-tight">
                      {f.label}
                    </span>
                    <span
                      className={`text-[10px] sm:text-xs ${
                        isActive
                          ? isBookmarked
                            ? 'text-amber-400'
                            : isIncorrect
                            ? 'text-red-400'
                            : 'text-teal-500'
                          : 'text-gray-400'
                      }`}
                    >
                      {c}問
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Count selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-hashtag mr-1.5 text-teal-600 text-sm"></i>
              出題数
            </label>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 20, 30, 50].map((n) => (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    count === n
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  {n}問
                </button>
              ))}
              <button
                onClick={() => setCount(9999)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  count === 9999
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                全問
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${
                  masteryFilter === 'bookmarked'
                    ? 'bg-amber-100'
                    : masteryFilter === 'incorrect'
                    ? 'bg-red-100'
                    : 'bg-teal-100'
                }`}
              >
                <i
                  className={`text-lg ${
                    masteryFilter === 'bookmarked'
                      ? 'ri-bookmark-fill text-amber-500'
                      : masteryFilter === 'incorrect'
                      ? 'ri-refresh-line text-red-500'
                      : 'ri-file-list-3-line text-teal-600'
                  }`}
                ></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {filterLabel}
                  {masteryFilter !== 'all' && (
                    <span
                      className={`ml-1 ${
                        masteryFilter === 'bookmarked'
                          ? 'text-amber-500'
                          : masteryFilter === 'incorrect'
                          ? 'text-red-500'
                          : 'text-teal-600'
                      }`}
                    >
                      （
                      {masteryFilterLabels.find(
                        (f) => f.key === masteryFilter
                      )?.label}
                      ）
                    </span>
                  )}
                  から
                  {count === 9999 ? '全問' : `${count}問`}出題
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  対象: {filteredCount}問 / ランダムに出題されます
                </p>
                {masteryFilter === 'incorrect' && (
                  <p className="text-xs text-gray-400 mt-1">
                    ※ 正解を2回以上記録すると間違えた問題リストから削除されます
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Start button */}
          <button
            onClick={handleStart}
            disabled={filteredCount === 0}
            className={`w-full py-3 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              masteryFilter === 'bookmarked'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                : masteryFilter === 'incorrect'
                ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            <i
              className={`text-base ${
                masteryFilter === 'bookmarked'
                  ? 'ri-bookmark-fill'
                  : masteryFilter === 'incorrect'
                  ? 'ri-refresh-line'
                  : 'ri-play-fill'
              }`}
            ></i>
            {filteredCount === 0
              ? '対象の問題がありません'
              : masteryFilter === 'bookmarked'
              ? '苦手問題を復習する'
              : masteryFilter === 'incorrect'
              ? '間違えた問題を復習する'
              : 'クイズをスタート'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
